import type {
  PillarEntry,
  PillarPrinciple,
  WorkflowStage,
  WorldEntryId,
} from "../types";

export const portfolioProfile = {
  name: "Johnny Leek",
  siteUrl: "https://johnnyleek.dev",
  resumeUrl: "/Johnny_Leek_Resume.pdf",
  home: {
    signal: "Software Engineer @ Microsoft · AI systems · reliable products",
    headlineLead: "I build complex systems",
    headlineEmphasis: "that earn trust.",
    introduction:
      "I connect AI agents, data platforms, reliability, and product design to turn hard-to-operate workflows into clearer decisions, safer recovery, and faster delivery.",
  },
  about: {
    signal: "Engineering profile",
    title: "I make complex systems easier to trust.",
    summary:
      "I'm a Software Engineer II on Microsoft's Dynamics 365 Business Performance Analytics team. I work where product design, engineering, and operations meet, tracing friction across the full workflow and turning it into clearer products, safer recovery paths, and delivery systems teams can depend on.",
    facts: [
      {
        label: "Current work",
        value: "Business Performance Analytics · Microsoft",
      },
      {
        label: "Education",
        value:
          "M.S. + B.S. Computer Science · Central Michigan University",
      },
      {
        label: "Working range",
        value:
          "React · TypeScript · Python · C# · SQL · AI Tooling · CI/CD · Azure DevOps",
      },
    ],
    themes: [
      {
        label: "Agent systems.",
        description:
          "I develop AI-assisted coding workflows and automations, and I've contributed to patent-pending agent sandboxing that makes expert context reusable.",
      },
      {
        label: "Data and operations.",
        description:
          "I move between Dataverse, Legacy systems, SQL investigations, incident management, and cross-repository integration to make failure legible.",
      },
      {
        label: "Product design and access.",
        description:
          "I learn from end users, admins, operators, and support engineers, then shape accessible React experiences around the decisions and recovery paths they actually need.",
      },
      {
        label: "Away from the keyboard.",
        description:
          "I enjoy off-roading and restoring classic cars, which is just another form of understanding a system by taking it apart.",
      },
    ],
  },
  aiFirst: {
    signal: "AI systems engineering pillar",
    title: "Use AI ambitiously. Trust it conditionally.",
    summary:
      "I use AI to increase engineering reach without lowering the evidence bar. That means orienting agents in the system, bounding their authority, validating outputs independently, and feeding what each run teaches back into the workflow. The result is not a one-shot answer, but a capable system that knows when to proceed and when human judgement is required.",
  },
  pipeline: {
    signal: "AI-first engineering · proof system",
    title: "A pipeline designed to challenge its own work.",
    summary:
      "This is one way I put the philosophy into practice. Specialized agents research, plan, challenge, implement, validate, ship, self-heal, and improve the workflow itself, returning to human judgement when ambiguity, authority, or exhausted retries make guessing unsafe.",
  },
  contact: {
    signal: "Work with me",
    title: "Let's work on the complicated part.",
    summary:
      "If you need an engineer who can connect AI systems, product judgement, reliability, and delivery, tell me what you're trying to make clearer, safer, or easier to ship.",
    email: "johnny@johnnyleek.dev",
    mailtoSubject: "Portfolio conversation",
  },
} as const;

export const worldNavigation: Array<{
  id: WorldEntryId;
  label: string;
  eyebrow: string;
  route: string;
}> = [
    {
      id: "about",
      label: "About Johnny",
      eyebrow: "Role, range, and approach",
      route: "/about",
    },
    {
      id: "workflow",
      label: "AI-first engineering",
      eyebrow: "Pillar · AI systems and validation",
      route: "/workflow",
    },
    {
      id: "reliability",
      label: "Reliability",
      eyebrow: "Pillar · diagnosis and recovery",
      route: "/pillar/reliability",
    },
    {
      id: "product",
      label: "Product judgement",
      eyebrow: "Pillar · customer confidence and access",
      route: "/pillar/product",
    },
    {
      id: "leverage",
      label: "Engineering effectiveness",
      eyebrow: "Pillar · tooling, defaults, and delivery",
      route: "/pillar/leverage",
    },
  ];

export const aiFirstPrinciples: PillarPrinciple[] = [
  {
    id: "system-orientation",
    label: "System orientation",
    title: "Understand before you delegate.",
    description:
      "AI inherits both power and blind spots from its context. I orient it in the architecture, constraints, history, and customer consequences before asking it to act.",
  },
  {
    id: "bounded-autonomy",
    label: "Bounded autonomy",
    title: "Give autonomy a boundary.",
    description:
      "Agents should have room to work without pretending every decision is safe to automate. Authority, retries, persistent state, and escalation paths make autonomy useful rather than reckless.",
  },
  {
    id: "independent-validation",
    label: "Independent validation",
    title: "Trust evidence, not confidence.",
    description:
      "A fluent answer is not proof. Plans are challenged before code, implementations are checked against requirements, and external build evidence must agree before the system can call itself complete.",
  },
  {
    id: "learning-loop",
    label: "Learning loop",
    title: "Make every run improve the next.",
    description:
      "Retries, bottlenecks, requested help, and retrospective evidence should change the workflow itself, so the next run needs less intervention without lowering its quality bar.",
  },
];

export const aiPipelineEvidence: PillarPrinciple = {
  id: "evidence",
  label: "Autonomous PR pipeline",
  title: "Make autonomy prove its own work.",
  description:
    "A multi-agent delivery system that researches, plans, challenges, implements, validates, ships, self-heals, and learns instead of asking one prompt to generate code and trust its own answer.",
  kind: "evidence",
};

export const aiFirstBranch = [...aiFirstPrinciples, aiPipelineEvidence];
export const PILLAR_EVIDENCE_ID = "evidence";

export const pillarEntries: PillarEntry[] = [
  {
    id: "reliability",
    label: "Reliability",
    title: "Systems should explain their own failure.",
    summary:
      "I treat reliability as a product property. State, ownership, and a safe path forward should be legible before an incident forces someone to reconstruct the system under pressure.",
    principles: [
      {
        id: "state",
        label: "Visible state",
        title: "Expose the state.",
        description:
          "Good systems make transitions, dependencies, and ownership visible enough that responders can move from symptoms to the responsible boundary.",
      },
      {
        id: "recovery",
        label: "Recovery design",
        title: "Design the recovery.",
        description:
          "Detection is incomplete without a safe, repeatable response. Recovery paths should be idempotent, observable, and built into the operating model.",
      },
    ],
    evidence: {
      title: "Tracing 80 paths to corruption.",
      summary:
        "I extended Finance and Operations SQL diagnostics for Dataverse compatibility, mapped 80 distinct corruption paths, and built idempotent resolution scripts that resolved severe blockers for thousands of active users.",
      audience:
        "Enterprise tenants, finance analysts, and the responders supporting them",
      decision:
        "Treat diagnosis and safe recovery as one continuous customer journey instead of stopping at detection.",
      impact: "80 corruption paths mapped · 800+ enterprise tenants unblocked",
    },
  },
  {
    id: "product",
    label: "Product judgement",
    title: "Confidence is part of the product.",
    summary:
      "I start with the workflow the customer will actually experience, not the architecture behind it. The hidden product inside a complicated system is often confidence: knowing what happened, what is safe, and what comes next.",
    principles: [
      {
        id: "customer-reality",
        label: "Customer reality",
        title: "Start with the lived workflow.",
        description:
          "End users, admins, operators, and support engineers each experience a different edge of the same system. Product judgement begins by understanding the whole journey they share.",
      },
      {
        id: "consequences",
        label: "Consequential clarity",
        title: "Hide complexity, not consequences.",
        description:
          "A simpler interface should still expose the state and decisions that matter. Compression is useful only when it preserves trust.",
      },
      {
        id: "access",
        label: "Inclusive access",
        title: "Accessibility is architecture.",
        description:
          "Access cannot be a finishing pass. Interaction, state management, semantics, and recovery all need to support the full range of people using the system.",
      },
    ],
    evidence: {
      title: "Turning hours into two clicks.",
      summary:
        "I engineered a focused React experience for a multi-environment Dataverse import and export flow, then drove WCAG and UX improvements that made the operation both simpler and independently validated for accessibility.",
      audience:
        "Finance administrators moving configuration safely across environments",
      decision:
        "Compress a fragile, multi-hour process into a guided two-click path without hiding consequential state.",
      impact: "Multi-hour workflow reduced to two clicks · Microsoft A11y Gold Medal awarded",
    },
  },
  {
    id: "leverage",
    label: "Engineering effectiveness",
    title: "Expertise should outlive the expert.",
    summary:
      "The strongest engineering improvement is one the next person receives without needing its original author in the room. I move context and judgement into defaults, tools, feedback loops, and release paths that help the team ship without waiting.",
    principles: [
      {
        id: "context",
        label: "Durable context",
        title: "Encode the context.",
        description:
          "Automated repository maps, generated documentation, agent workflows, and durable handoffs turn scattered knowledge into an operating system the team can share.",
      },
      {
        id: "defaults",
        label: "Better defaults",
        title: "Improve the default path.",
        description:
          "I believe that efficiency starts to compound when the reliable and efficient path is also the easiest one to follow, at every step from local testing through release.",
      },
      {
        id: "release-flow",
        label: "Release flow",
        title: "Make shipping routine.",
        description:
          "CI/CD should empower a team to release often. I remove checks that add delay without useful signal, preserve the gates that protect customers, and make the release path fast, stable, and predictable.",
      },
    ],
    evidence: {
      title: "Making the fast path reliable.",
      summary:
        "I architected scheduled Azure Foundry agents to make repository context reusable, then applied the same default-path thinking to delivery: parallelizing tests, replacing unstable external dependencies, and inverting a legacy two-repository relationship so releases were faster and less brittle.",
      audience:
        "Engineers shipping fixes and features, plus customers waiting on dependable delivery",
      decision:
        "Remove ceremony and unstable checks that do not improve confidence, then invest in the fast, reliable gates that let the team release often.",
      impact: "7 days removed from our release cycle · pipelines 40% faster on average",
    },
  },
];

export const workflowStages: WorkflowStage[] = [
  {
    id: "orient",
    label: "01",
    title: "Orient the run",
    role: "PR orchestrator",
    kind: "orchestrate",
    description:
      "Resolve the work item and repository, establish an isolated worktree, register the pipeline, and persist enough state for every specialist to resume from evidence instead of memory.",
    autonomy:
      "Sets up the run, checks concurrency, advances the work item, and records stage state automatically.",
    qualityGate:
      "Refuses to touch code until the target repository and work item are unambiguous.",
    humanCheckpoint:
      "Only when the request cannot be resolved safely or authentication blocks the run.",
    artifact: "Isolated worktree + registered pipeline",
  },
  {
    id: "research",
    label: "02",
    title: "Research the system",
    role: "pr-researcher",
    kind: "work",
    description:
      "Inspect architecture, conventions, dependencies, history, and likely failure modes before a solution is allowed to narrow the field.",
    autonomy:
      "Explores the codebase and writes a durable report for every downstream agent.",
    qualityGate:
      "Separates evidence from assumptions and records unresolved risks before planning.",
    humanCheckpoint:
      "None unless required context is inaccessible or contradictory.",
    artifact: "Research report",
  },
  {
    id: "plan",
    label: "03",
    title: "Plan the change",
    role: "pr-planner",
    kind: "work",
    description:
      "Turn the research into a concrete sequence of files, behavioral contracts, tests, migration risks, and completion criteria.",
    autonomy:
      "Consumes the research report and produces an implementation-ready contract.",
    qualityGate:
      "The plan must be specific enough for a different agent to implement without inventing missing behavior.",
    humanCheckpoint:
      "None while the plan remains internally resolvable.",
    artifact: "Implementation plan",
  },
  {
    id: "verify",
    label: "04",
    title: "Verify before code",
    role: "pr-verifier",
    kind: "gate",
    description:
      "An independent agent attacks the plan against the research and source, looking for missing surfaces, false assumptions, hidden coupling, and unverifiable steps.",
    autonomy:
      "Rejects weak plans and returns precise feedback to the planner for another pass.",
    qualityGate:
      "Planning and verification can iterate up to three times before code is written.",
    humanCheckpoint:
      "Escalates only after three rejected plans leave the system unable to converge safely.",
    artifact: "Verified plan",
  },
  {
    id: "implement",
    label: "05",
    title: "Implement in isolation",
    role: "pr-implementer",
    kind: "work",
    description:
      "Execute the verified plan inside the isolated worktree, follow repository conventions, keep the change coherent, and leave a report that makes validation evidence-based.",
    autonomy:
      "Writes the code and records significant actions in persistent agent state.",
    qualityGate:
      "Implementation is constrained by the verified plan rather than improvising a new design mid-run.",
    humanCheckpoint:
      "Only for blocked permissions, unsafe ambiguity, or missing authority.",
    artifact: "Implementation report",
  },
  {
    id: "validate",
    label: "06",
    title: "Validate and repair",
    role: "pr-validator",
    kind: "gate",
    description:
      "Check the implementation against the plan and research with tests, static analysis, diff review, and requirement-level validation rather than accepting a successful build as proof.",
    autonomy:
      "Returns failures to the implementer and can drive two focused repair attempts.",
    qualityGate:
      "Critical gaps block PR creation even when the code appears locally complete.",
    humanCheckpoint:
      "Escalates when critical issues remain after the bounded repair loop.",
    artifact: "Validation report",
  },
  {
    id: "ship",
    label: "07",
    title: "Ship and self-heal",
    role: "PR orchestrator + pr-build-watcher",
    kind: "orchestrate",
    description:
      "Create and link the pull request, publish the run evidence, then watch CI. Build failures are diagnosed from logs and repaired automatically instead of being handed back immediately.",
    autonomy:
      "The build watcher can diagnose, patch, and retry failures up to three times.",
    qualityGate:
      "A PR is not treated as complete until its external build evidence is healthy.",
    humanCheckpoint:
      "Escalates after three failed self-heal attempts or when credentials prevent diagnosis.",
    artifact: "Pull request + Craft report + green build",
  },
  {
    id: "retrospect",
    label: "08",
    title: "Retrospect and improve",
    role: "retrospective-writer",
    kind: "learn",
    description:
      "Analyze stage durations, retry counts, research misses, missing context, and every place the run needed help, then convert those patterns into concrete improvements for future agents and skills.",
    autonomy:
      "Runs after success or failure and feeds the next improvement cycle with evidence from the full pipeline.",
    qualityGate:
      "Recommendations must name the bottleneck and propose specific prompt or workflow diffs, not generic lessons.",
    humanCheckpoint:
      "Durable changes that alter policy or safety boundaries remain reviewable before adoption.",
    artifact: "Retrospective + skill improvement diffs",
  },
];

export const getPillarEntry = (slug: string) =>
  pillarEntries.find((entry) => entry.id === slug);

export const getPillarBranch = (pillar: PillarEntry): PillarPrinciple[] => [
  ...pillar.principles,
  {
    id: PILLAR_EVIDENCE_ID,
    label: "Connected evidence",
    title: pillar.evidence.title,
    description: pillar.evidence.summary,
    kind: "evidence",
  },
];
